// PLA Toys playground: 3D toys you can click, flex, twist and throw.
// Runs on its own (playground/index.html, models in playground/models) and inside the Shopify
// theme (sections/main-play.liquid, models as theme assets). The page says which through
// data attributes on the .pg element:
//   data-assets  where the .glb models live
//   data-shop    prefix for product links ("" on the store itself)
// and, in the theme, a #play-products JSON block maps each product to its variant ids so the
// buy button can add the exact one you played with to the cart.
import * as THREE from 'https://cdn.jsdelivr.net/npm/three@0.170.0/+esm';
import { GLTFLoader } from 'https://cdn.jsdelivr.net/npm/three@0.170.0/examples/jsm/loaders/GLTFLoader.js/+esm';
import { RoomEnvironment } from 'https://cdn.jsdelivr.net/npm/three@0.170.0/examples/jsm/environments/RoomEnvironment.js/+esm';
import RAPIER from 'https://cdn.jsdelivr.net/npm/@dimforge/rapier3d-compat@0.19.3/rapier.mjs';

// ───────────────────────── the toys ─────────────────────────
// Each GLB has static parts in the toy frame (cm, Y up, origin at the bottom centre), a
// pressable `button` node with its own origin at its bottom and extras.seatY, and
// optionally a `lid` node built the same way. A `multi` GLB holds several toys, and every
// node carries extras { toy, role, seat }.
// the eight colours both spirals come in
const SPIRAL_COLORS = [['Pink', '#f2a7b8'], ['Red', '#d8403e'], ['Black', '#2b2830'], ['Green', '#62bf6e'], ['Orange', '#f3934a'], ['Tan', '#d9b58b'], ['White', '#f3f0ea'], ['Yellow', '#f5d04e']];
const CREDIT = {
  astrodex: '<a href="https://makerworld.com/en/@AstroDex_3D" target="_blank" rel="noopener">AstroDex</a>',
  fillifuu: '<a href="https://makerworld.com/en/@FilliFuu" target="_blank" rel="noopener">FilliFuu</a>',
  nukddd: '<a href="https://makerworld.com/en/@NUKDDD" target="_blank" rel="noopener">NUKDDD</a>',
};
const TOYS = {
  dumpling: {
    no: '01', short: 'Dumpling', model: 'dumpling.glb', button: 'dumpling', lid: 'lid',
    title: 'Basket <em>Dumpling.</em>',
    lede: 'A soft little bun in a bamboo basket. Pull the lid off, give it a click, toss it around.',
    how: 'Pull the lid straight up until it pops. Tap the dumpling to click it. Grab anything and throw it. Drag the empty space to look around.',
    loading: 'Steaming the dumpling…', credit: 'astrodex',
    handle: 'basket-dumpling-clicker', faceCamera: true, price: '$5', buy: (c) => `Get the ${c} one`,
    colors: [['Pink', '#f1a9b6'], ['Green', '#8fd6a2'], ['Orange', '#f5874a'], ['White', '#f2eee8'], ['Yellow', '#f6cf5a']],
    recolor: ['body', 'cheeks'], colliders: 'basket',
    press: 0.3, squash: 0.07, buttonRough: 0.5, pressHint: 'Tap the dumpling',
  },
  burger: {
    no: '02', short: 'Burger', model: 'burger.glb', button: 'bun', lid: null,
    title: 'Burger <em>Clicker.</em>',
    lede: 'A stacked little cheeseburger with a keyboard switch under the top bun. Give it a press, then toss it around.',
    how: 'Tap the top bun to click it, or hold Space. Grab the burger and throw it. Drag the empty space to look around.',
    loading: 'Flipping the patty…', credit: 'astrodex',
    handle: 'burger-clicker', price: '$5', buy: () => 'Get the Burger',
    colors: null, recolor: [], colliders: 'hull',
    press: 0.3, squash: 0.03, buttonRough: null, pressHint: 'Tap the top bun',
    // the fillings give a little under the bun, squashing down toward the bottom bun
    squashParts: ['lettuce', 'patty', 'cheese', 'tomato'], squashBase: 0.72, squashAmount: 0.06,
  },
  chocolate: {
    // all four bars at once; each node in this GLB says which bar it belongs to (extras.toy)
    no: '03', short: 'Chocolate', model: 'chocolate.glb', multi: true, button: null, lid: null,
    title: 'Chocolate <em>Bars.</em>',
    lede: 'The whole set: one, two, three and four clicks. Every square on top is its own keyboard switch.',
    how: 'Tap any square to click it, or hold Space for the last one you pressed. Grab a bar and throw it. Drag the empty space to look around.',
    loading: 'Tempering the chocolate…', credit: 'astrodex',
    handle: 'chocolate-bundle', price: '$20', buy: () => 'Get all four',
    colors: null, recolor: [], colliders: 'hull',
    press: 0.3, squash: 0, jiggle: 0, buttonRough: null, pressHint: 'Tap a square',
    spacing: 6.4, viewScale: 1.45,
  },
  macaroon: {
    no: '04', short: 'Macaroon', model: 'macaroon.glb', button: 'shell', lid: null,
    title: 'Macaroon <em>Clicker.</em>',
    lede: 'A pastel macaron with a keyboard switch tucked into the cream. Press the top, then toss it around.',
    how: 'Tap the top shell to click it, or hold Space. Grab it and throw it. Drag the empty space to look around.',
    loading: 'Piping the cream…', credit: 'astrodex',
    handle: 'macaroon-clicker', price: '$5', buy: (c) => `Get the ${c} one`,
    colors: [['Pink', '#f1a3b4'], ['Orange', '#f7a25e'], ['Red', '#e0484a'], ['Yellow', '#f6d36a']],
    recolor: ['shell'], colliders: 'hull',
    press: 0.3, squash: 0.02, buttonRough: null, pressHint: 'Tap the top shell',
  },
  egg: {
    no: '05', short: 'Egg', model: 'egg.glb', button: 'yolk', lid: null,
    title: 'Egg <em>Clicker.</em>',
    lede: 'A sunny-side-up egg. The yolk sits on a keyboard switch, so it clicks when you press it.',
    how: 'Tap the yolk to click it, or hold Space. Grab the egg and throw it. Drag the empty space to look around.',
    loading: 'Cracking the egg…', credit: 'astrodex',
    handle: 'egg-clicker', price: '$5', buy: () => 'Get the Egg',
    colors: null, recolor: [], colliders: 'hull',
    press: 0.25, squash: 0.04, buttonRough: null, pressHint: 'Tap the yolk',
  },
  waffle: {
    no: '06', short: 'Waffle', model: 'waffle.glb', button: 'top', lid: null,
    title: 'Waffle <em>Clicker.</em>',
    lede: 'An ice cream waffle sandwich. The top waffle is a keyboard switch.',
    how: 'Tap the top waffle to click it, or hold Space. Grab it and throw it. Drag the empty space to look around.',
    loading: 'Scooping the ice cream…', credit: 'astrodex',
    handle: 'waffle-clicker', price: '$5', buy: () => 'Get the Waffle',
    colors: null, recolor: [], colliders: 'hull',
    press: 0.25, squash: 0.02, buttonRough: null, pressHint: 'Tap the top waffle',
  },
  // Flexi animals: every bone is its own body, joined where the print's segments interlock.
  panda: {
    no: '07', short: 'Panda', viewAz: 1.15, model: 'flexi-panda.glb', flexi: true, button: null, lid: null,
    title: 'Flexi <em>Panda.</em>',
    lede: 'Printed in one go with every joint already linked. Pick it up and it flops like a little stuffed bear.',
    how: 'Grab any part of the panda and swing it around. Drag the empty space to look around.',
    loading: 'Waking the panda…', credit: 'fillifuu',
    handle: 'panda', price: '$7', buy: () => 'Get the Panda',
    colors: null, recolor: [], press: 0.3, squash: 0, pressHint: '', gravity: 34, viewScale: 1.1, bend: 0.3, sideBend: 0.14, limbBend: 0.3, limbFriction: 1.6,
  },
  puppy: {
    no: '08', short: 'Puppy', viewAz: 1.15, model: 'flexi-sleepy-puppy.glb', flexi: true, button: null, lid: null,
    title: 'Flexi <em>Sleepy Puppy.</em>',
    lede: 'A curled-up puppy printed with all its joints linked. Pick it up by the scruff and watch it flop.',
    how: 'Grab any part of the puppy and swing it around. Drag the empty space to look around.',
    loading: 'Waking the puppy…', credit: 'fillifuu',
    handle: 'sleepy-puppy', price: '$7', buy: () => 'Get the Sleepy Puppy',
    colors: null, recolor: [], press: 0.3, squash: 0, pressHint: '', gravity: 34, viewScale: 1.15, bend: 0.32, sideBend: 0.15, limbBend: 0.55,
  },
  orca: {
    no: '09', short: 'Orca', viewAz: 1.15, model: 'flexi-orca.glb', flexi: true, button: null, lid: null,
    title: 'Flexi <em>Orca.</em>',
    lede: 'A bendy orca made of interlocking plates. Grab it by the tail and make it swim.',
    how: 'Grab any part of the orca and swing it around. Drag the empty space to look around.',
    loading: 'Filling the pool…', credit: 'fillifuu',
    handle: 'orcha', price: '$7', buy: () => 'Get the Orca',
    // each row of plates bends about 15° up or down (the whole body curls into a C like the
    // real one) but only about 6° sideways before the plates jam
    colors: null, recolor: [], press: 0.3, squash: 0, pressHint: '', gravity: 34, viewScale: 1.1, bend: 0.26, sideBend: 0.1, limbBend: 0.35,
  },
  // Twisty spiral eggs: interlocking helical blades that slide along each other to open.
  dragon: {
    no: '10', short: 'Dragon Egg', model: 'spiral-dragon.glb', spiral: true, button: null, lid: null,
    title: 'Dragon <em>Egg.</em>',
    lede: 'A twisty egg covered in dragon scales. Pull it upward and the scales slide along each other into a spiral tower.',
    how: 'Drag up on the egg to twist it open, drag down to close it. Space or the button does it for you. When it is shut you can grab it and throw it.',
    loading: 'Warming the egg…', credit: 'nukddd',
    handle: 'dragon-egg-spiral', price: '$6', buy: (c) => `Get the ${c} one`,
    colors: SPIRAL_COLORS, recolor: ['egg'], press: 0.3, squash: 0, pressHint: '', viewScale: 1.9, openRise: 1.15,
  },
  eggspiral: {
    no: '11', short: 'Egg Spiral', model: 'spiral-egg.glb', spiral: true, button: null, lid: null,
    title: 'Egg <em>Spiral.</em>',
    lede: 'A smooth twisty egg made of spiral blades. Pull it up and it unwinds into a twisting tower.',
    how: 'Drag up on the egg to twist it open, drag down to close it. Space or the button does it for you. When it is shut you can grab it and throw it.',
    loading: 'Winding the spiral…', credit: 'nukddd',
    handle: 'egg-spiral', price: '$6', buy: (c) => `Get the ${c} one`,
    colors: SPIRAL_COLORS, recolor: ['egg'], press: 0.3, squash: 0, pressHint: '', viewScale: 0.95, openRise: 1.2,
  },
};
const ROOT = document.querySelector('.pg');
const ASSETS = ROOT.dataset.assets ?? 'models/';
const SHOP = ROOT.dataset.shop ?? 'https://pla-toys.com';
// variant ids per product handle (only on the store): { handle: { variants: { "Pink": 123 } } }
let PRODUCTS = {};
try { PRODUCTS = JSON.parse(document.getElementById('play-products')?.textContent || '{}'); } catch {}
// which toy: the page can name it (a product page's 3D view does), else ?toy= in the URL
const toyKey = ROOT.dataset.toy || new URLSearchParams(location.search).get('toy');
// embedded in a product page's gallery: just the toy, no title, panel or captions
const EMBED = ROOT.classList.contains('embed');
const T = TOYS[toyKey] || TOYS.dumpling;

// ───────────────────────── constants (toy frame: cm, Y up, origin at the bottom centre) ─────────────────────────
const BASKET_R = 1.85, BASKET_H = 1.22, SHOULDER = 0.749;
const RIM_R = 1.69;           // the narrower band the lid slides over
const LID_H = 2.82, LID_TOP = 0.42, LID_WALL_R = 1.8, LID_WALLS = 24;
const PRESS_DEPTH = T.press;  // button travel
// gravity, tuned for a toy-sized feel (true scale would be 981 cm/s²); flexis float down
// slowly, like on the moon, so a swing reads as a soft drift instead of a snap
const G = T.gravity ?? 62;
const STEP = 1 / 120;
const COLORS = T.colors || [];
const reduceMotion = matchMedia('(prefers-reduced-motion: reduce)').matches;
let LID_SEAT = SHOULDER, BTN_SEAT = 1.168; // overwritten from the GLB extras
let BTN_TOP = 1.5; // the button's height, for placing hints

// ───────────────────────── renderer / scene ─────────────────────────
const stage = document.getElementById('stage');
const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
renderer.setPixelRatio(Math.min(devicePixelRatio, 2));
renderer.outputColorSpace = THREE.SRGBColorSpace;
renderer.toneMapping = THREE.NeutralToneMapping;
renderer.shadowMap.enabled = true;
renderer.shadowMap.type = THREE.PCFSoftShadowMap;
stage.appendChild(renderer.domElement);

const scene = new THREE.Scene();
const pmrem = new THREE.PMREMGenerator(renderer);
scene.environment = pmrem.fromScene(new RoomEnvironment(), 0.04).texture;
scene.environmentIntensity = 0.42;

const sun = new THREE.DirectionalLight(0xfff1de, 3.1);
sun.position.set(5, 11, 6);
sun.castShadow = true;
sun.shadow.mapSize.set(2048, 2048);
Object.assign(sun.shadow.camera, { left: -9, right: 9, top: 9, bottom: -9, near: 1, far: 40 });
sun.shadow.bias = -0.0004;
sun.shadow.normalBias = 0.02;
scene.add(sun);
scene.add(new THREE.HemisphereLight(0xfffaf2, 0xcbbca6, 0.32));
const rim = new THREE.DirectionalLight(0xe9e4ff, 0.9);
rim.position.set(-6, 5, -7);
scene.add(rim);

const ground = new THREE.Mesh(new THREE.PlaneGeometry(80, 80), new THREE.ShadowMaterial({ opacity: 0.17 }));
ground.rotation.x = -Math.PI / 2;
ground.receiveShadow = true;
scene.add(ground);

const camera = new THREE.PerspectiveCamera(26, 1, 0.1, 200);
const view = { az: T.viewAz ?? 0.35, pol: 1.08, dist: 27, zoom: 1, target: new THREE.Vector3(0, 1.3, 0) };
function placeCamera() {
  const s = Math.sin(view.pol), d = view.dist * view.zoom;
  camera.position.set(view.target.x + d * s * Math.sin(view.az), view.target.y + d * Math.cos(view.pol), view.target.z + d * s * Math.cos(view.az));
  camera.lookAt(view.target);
}

// ───────────────────────── visuals ─────────────────────────
const toyGroup = new THREE.Group();          // follows the toy body
const btnPivot = new THREE.Group();
const lidPivot = new THREE.Group();          // the lid while it's on
toyGroup.add(btnPivot, lidPivot);
scene.add(toyGroup);
const lidGroup = new THREE.Group();          // the lid while it's off
scene.add(lidGroup);
let lidMesh, btnMesh, basketMesh, squashers = [];
const bodyMats = [];

// A unit is something you can pick up (a rigid body and its visual); a button is a part
// that presses. Most toys are one unit with one button; the chocolate set is four bars with
// ten squares between them.
const units = [];
const buttons = [];
let activeBtn = null;
function makeUnit(rb, group) {
  const u = { rb, group, jig: new THREE.Vector3(), jigV: new THREE.Vector3(), prevVel: new THREE.Vector3(), accF: new THREE.Vector3(), tippedFor: 0, righting: false, rightT: 0 };
  group.userData.unit = u;
  units.push(u);
  return u;
}
function makeButton(unit, pivot, seat) {
  const b = { unit, pivot, seat, press: 0, pressV: 0, held: false, clicked: false, bottomed: false };
  pivot.userData.btn = b; pivot.userData.part = 'button';
  pivot.position.copy(seat);
  buttons.push(b);
  return b;
}

// ───────────────────────── state ─────────────────────────
const S = {
  lidOn: !!T.lid, clicks: 0, bestCps: 0, clickTimes: [],
  lidLift: 0, snapping: null, time: 0, noSnapUntil: 0, lidReleasedAt: -9,
  popped: false, thrown: false, sound: true, arena: 6,
};
Object.defineProperty(S, 'press', { get: () => buttons[0]?.press ?? 0 }); // for the test hook

// ───────────────────────── sound (synthesised, no files) ─────────────────────────
let ac = null, noiseBuf = null;
function audio() {
  if (!ac) {
    ac = new (window.AudioContext || window.webkitAudioContext)();
    noiseBuf = ac.createBuffer(1, ac.sampleRate * 0.5, ac.sampleRate);
    const d = noiseBuf.getChannelData(0);
    for (let i = 0; i < d.length; i++) d[i] = Math.random() * 2 - 1;
  }
  if (ac.state === 'suspended') ac.resume();
  return ac;
}
function noise(t, dur, freq, q, gain, type = 'bandpass') {
  const src = ac.createBufferSource(); src.buffer = noiseBuf;
  const bp = ac.createBiquadFilter(); bp.type = type; bp.frequency.value = freq; bp.Q.value = q;
  const g = ac.createGain(); g.gain.setValueAtTime(0.0001, t); g.gain.linearRampToValueAtTime(gain, t + 0.0006); g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
  src.connect(bp).connect(g).connect(ac.destination); src.start(t, Math.random() * 0.4); src.stop(t + dur + 0.02);
}
// A ringing resonance, for the hollow printed shell the switch sits in.
function ring(t, dur, freq, gain) {
  const src = ac.createBufferSource(); src.buffer = noiseBuf;
  const bp = ac.createBiquadFilter(); bp.type = 'bandpass'; bp.frequency.value = freq; bp.Q.value = 18;
  const g = ac.createGain(); g.gain.setValueAtTime(gain, t); g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
  src.connect(bp).connect(g).connect(ac.destination); src.start(t, Math.random() * 0.4); src.stop(t + dur + 0.02);
}
function tone(t, dur, f0, f1, gain, type = 'sine') {
  const o = ac.createOscillator(); o.type = type;
  o.frequency.setValueAtTime(f0, t); o.frequency.exponentialRampToValueAtTime(f1, t + dur);
  const g = ac.createGain(); g.gain.setValueAtTime(gain, t); g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
  o.connect(g).connect(ac.destination); o.start(t); o.stop(t + dur + 0.02);
}
const on = () => S.sound && ac;
const sfx = {
  // Cherry MX Blue. The click jacket snaps partway down: a hard, bright tick with a metallic edge.
  blueDown() { if (!on()) return; const t = ac.currentTime; const j = 1 + (Math.random() - 0.5) * 0.06;
    noise(t, 0.007, 3800 * j, 0.7, 0.9, 'highpass');
    noise(t, 0.012, 5600 * j, 4, 0.55);
    tone(t, 0.016, 7200 * j, 6500, 0.06, 'triangle');
    ring(t + 0.001, 0.05, 1350 * j, 0.22); },
  // ...then the stem bottoms out in the housing: lower, duller plastic.
  bottomOut(v = 1) { if (!on()) return; const t = ac.currentTime; const a = 0.5 + 0.5 * Math.min(v, 1);
    noise(t, 0.022, 1750, 1.3, 0.42 * a);
    tone(t, 0.035, 520, 330, 0.12 * a);
    ring(t, 0.06, 880, 0.12 * a); },
  // On the way up the jacket resets with a second, softer click, then the stem tops out.
  blueUp() { if (!on()) return; const t = ac.currentTime; const j = 1 + (Math.random() - 0.5) * 0.06;
    noise(t, 0.006, 4200 * j, 0.7, 0.5, 'highpass');
    noise(t, 0.01, 5000 * j, 4, 0.3);
    ring(t, 0.04, 1350 * j, 0.12); },
  topOut() { if (!on()) return; const t = ac.currentTime; noise(t, 0.012, 2300, 1.5, 0.14); },
  slide(e) { if (!on()) return; const t = ac.currentTime; const j = 0.9 + e * 0.25 + Math.random() * 0.08;
    noise(t, 0.012, 2600 * j, 3, 0.18); ring(t, 0.03, 1700 * j, 0.06); },
  pop() { if (!on()) return; const t = ac.currentTime; tone(t, 0.12, 380, 85, 0.5); noise(t, 0.05, 950, 1.2, 0.3); },
  snap() { if (!on()) return; const t = ac.currentTime; noise(t, 0.02, 2400, 2, 0.45); tone(t, 0.06, 240, 140, 0.35); ring(t, 0.08, 760, 0.15); },
  knock(a) { if (!on()) return; const t = ac.currentTime;
    noise(t, 0.045 + a * 0.04, 1300 + Math.random() * 600, 2.2, 0.55 * a); tone(t, 0.07, 230 + Math.random() * 60, 120, 0.4 * a); ring(t, 0.07, 700 + Math.random() * 300, 0.12 * a); },
};

// ───────────────────────── physics (Rapier) ─────────────────────────
await RAPIER.init();
const world = new RAPIER.World({ x: 0, y: -G, z: 0 });
world.timestep = STEP;
const events = new RAPIER.EventQueue(true);
const PLA = { friction: 0.5, restitution: 0.18 };

function col(desc, body, { friction = PLA.friction, restitution = PLA.restitution, density = 1, groups = null } = {}) {
  if (groups !== null) desc.setCollisionGroups(groups);
  desc.setFriction(friction).setRestitution(restitution).setDensity(density)
    .setActiveEvents(RAPIER.ActiveEvents.CONTACT_FORCE_EVENTS).setContactForceEventThreshold(0);
  return world.createCollider(desc, body);
}

// the desk
const deskBody = world.createRigidBody(RAPIER.RigidBodyDesc.fixed());
world.createCollider(RAPIER.ColliderDesc.cuboid(60, 0.5, 60).setTranslation(0, -0.5, 0).setFriction(0.6).setRestitution(0.2), deskBody);

// the toy: basket + dumpling, plus the lid's colliders while it's on
const toy = world.createRigidBody(RAPIER.RigidBodyDesc.dynamic().setTranslation(0, 7, 0)
  .setLinearDamping(0.05).setAngularDamping(0.12).setCcdEnabled(true));
const U0 = makeUnit(toy, toyGroup);
const RB = 0.04; // rounded edges roll and slide like a real print instead of catching on facets
function basketColliders() {
  col(RAPIER.ColliderDesc.roundCylinder(SHOULDER / 2 - RB, BASKET_R - RB, RB).setTranslation(0, SHOULDER / 2, 0), toy);
  col(RAPIER.ColliderDesc.roundCylinder((BASKET_H - SHOULDER) / 2 - RB, RIM_R - RB, RB).setTranslation(0, SHOULDER + (BASKET_H - SHOULDER) / 2, 0), toy);
}
// Ballast: a dense hidden slab in the base drops the centre of mass so the toy is hard to tip.
// Round toys get a disc; the bars get a block sized to their footprint.
function ballast(rb, box) {
  if (!box) { col(RAPIER.ColliderDesc.cylinder(0.09, 1.6).setTranslation(0, 0.1, 0), rb, { density: 14 }); return; }
  const sx = (box.max.x - box.min.x) * 0.36, sz = (box.max.z - box.min.z) * 0.36;
  const cx = (box.max.x + box.min.x) / 2, cz = (box.max.z + box.min.z) / 2;
  col(RAPIER.ColliderDesc.cuboid(sx, 0.09, sz).setTranslation(cx, 0.1, cz), rb, { density: 14 });
}
let lidOnCols = [];
let lidBody = null; // exists only while the lid is off

// The lid is a real cup: a top disc and a ring of wall slabs, open at the bottom, so it sits
// over the bun and can slide back down onto the basket.
function addLidColliders(body, y0) {
  const cols = [];
  cols.push(col(RAPIER.ColliderDesc.roundCylinder(LID_TOP / 2 - RB, BASKET_R - RB, RB).setTranslation(0, y0 + LID_H - LID_TOP / 2, 0), body));
  const wallH = LID_H - LID_TOP;
  const halfW = (Math.PI * LID_WALL_R / LID_WALLS) * 1.1;
  for (let i = 0; i < LID_WALLS; i++) {
    const a = (i / LID_WALLS) * Math.PI * 2;
    const q = new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(0, 1, 0), -a);
    cols.push(col(RAPIER.ColliderDesc.cuboid(0.05, wallH / 2, halfW)
      .setTranslation(Math.cos(a) * LID_WALL_R, y0 + wallH / 2, Math.sin(a) * LID_WALL_R)
      .setRotation({ x: q.x, y: q.y, z: q.z, w: q.w }), body));
  }
  return cols;
}

// helpers
const tq = (b) => { const r = b.rotation(); return new THREE.Quaternion(r.x, r.y, r.z, r.w); };
const tv = (v) => new THREE.Vector3(v.x, v.y, v.z);
const toWorld = (b, local) => local.clone().applyQuaternion(tq(b)).add(tv(b.translation()));
const toLocal = (b, world) => world.clone().sub(tv(b.translation())).applyQuaternion(tq(b).invert());
const upOf = (b) => new THREE.Vector3(0, 1, 0).applyQuaternion(tq(b));
function velAt(b, p) {
  const w = tv(b.angvel());
  return tv(b.linvel()).add(w.cross(p.clone().sub(tv(b.worldCom()))));
}
function impulseAt(b, f, p) { b.applyImpulseAtPoint({ x: f.x, y: f.y, z: f.z }, { x: p.x, y: p.y, z: p.z }, true); }

// sounds and squish from real contact forces
const lastHit = new Map();
function drainContacts() {
  events.drainContactForceEvents((e) => {
    const c1 = world.getCollider(e.collider1()), c2 = world.getCollider(e.collider2());
    if (T.flexi) { flexKnock(e, c1, c2); return; }
    for (const c of [c1, c2]) {
      const b = c && c.parent();
      if (!b || !b.isDynamic()) continue;
      const s = e.totalForceMagnitude() / (b.mass() * G); // 1 ≈ just resting
      const key = b.handle;
      if (s > 7 && S.time - (lastHit.get(key) ?? -1) > 0.07) {
        lastHit.set(key, S.time);
        sfx.knock(Math.min((s - 7) / 30, 1));
        const u = units.find((x) => x.rb === b);
        if (u) u.jigV.y -= Math.min(s * 0.02, 1.2);
      }
    }
  });
}

// A flexi is a dozen or more light pieces, and each one touching down would click on its
// own: one knock for the whole animal, only off the desk (not its pieces rubbing), only
// for a piece that's really moving into it, and no more than about seven a second.
let flexMass = 0;
function flexKnock(e, c1, c2) {
  const b1 = c1 && c1.parent(), b2 = c2 && c2.parent();
  const b = b1 === deskBody ? b2 : b2 === deskBody ? b1 : null;
  if (!b || !b.isDynamic()) return;
  flexMass ||= units.reduce((m, u) => m + u.rb.mass(), 0);
  const v = b.linvel(), into = -v.y;
  const s = e.totalForceMagnitude() / (flexMass * G);
  if (into < 1.5 || s < 1.5 || S.time - (lastHit.get('flexi') ?? -1) < 0.14) return;
  lastHit.set('flexi', S.time);
  sfx.knock(Math.min((into - 1.5) / 8, 1) * 0.8);
}

function spawn() {
  if (T.lid && !S.lidOn) attachLidInstant();
  if (T.flexi) {
    units.forEach((u, i) => {
      const r = flexRest[i];
      u.rb.setTranslation({ x: r.x, y: r.y + 5 + STAGE_TOP, z: r.z }, true);
      u.rb.setRotation({ x: 0, y: 0, z: 0, w: 1 }, true);
      u.rb.setLinvel({ x: 0, y: -2, z: 0 }, true);
      u.rb.setAngvel({ x: 0, y: 0, z: 0 }, true);
    });
    return;
  }
  const n = units.length;
  units.forEach((u, i) => {
    const multi = n > 1;
    // a toy with a face (its +Z) lands looking at the camera, give or take a little
    const yaw = T.faceCamera ? view.az + (Math.random() - 0.5) * 0.3 : multi ? (Math.random() - 0.5) * 0.6 : Math.random() * 6.28;
    const q = new THREE.Quaternion().setFromEuler(new THREE.Euler(0.12 + Math.random() * 0.08, yaw, 0.05));
    const x = multi ? (i - (n - 1) / 2) * T.spacing : (Math.random() - 0.5) * 1.5;
    u.rb.setTranslation({ x, y: 7 + (multi ? i * 1.4 : 0), z: (Math.random() - 0.5) * 0.8 }, true);
    u.rb.setRotation({ x: q.x, y: q.y, z: q.z, w: q.w }, true);
    u.rb.setLinvel({ x: 0, y: -2, z: 0 }, true);
    u.rb.setAngvel({ x: 0, y: T.faceCamera ? 0 : multi ? 0.6 : 1.6, z: 0 }, true);
  });
}

// ───────────────────────── lid on / off ─────────────────────────
function detachLid(extraVel) {
  if (!S.lidOn) return;
  S.lidOn = false;
  const pos = toWorld(toy, new THREE.Vector3(0, LID_SEAT + S.lidLift, 0));
  const q = tq(toy);
  lidOnCols.forEach((c) => world.removeCollider(c, true)); lidOnCols = [];
  lidBody = world.createRigidBody(RAPIER.RigidBodyDesc.dynamic()
    .setTranslation(pos.x, pos.y, pos.z).setRotation({ x: q.x, y: q.y, z: q.z, w: q.w })
    .setLinearDamping(0.05).setAngularDamping(0.12).setCcdEnabled(true));
  addLidColliders(lidBody, 0);
  const v = velAt(toy, pos).add(extraVel || new THREE.Vector3());
  lidBody.setLinvel(v, true);
  lidBody.setAngvel(toy.angvel(), true);
  lidPivot.remove(lidMesh); lidGroup.add(lidMesh);
  lidMesh.position.set(0, 0, 0); lidMesh.quaternion.identity();
  S.lidLift = 0; lidPivot.position.y = LID_SEAT;
  // the basket kicks back as the seal lets go
  const up = upOf(toy);
  impulseAt(toy, up.clone().multiplyScalar(-toy.mass() * 0.9), toWorld(toy, new THREE.Vector3(0, BASKET_H, 0)));
  U0.jigV.y += 1.6;
  sfx.pop();
  S.popped = true;
  S.noSnapUntil = S.time + 0.6;
  updateLidButton();
}

function putLidOnToy() {
  lidMesh.removeFromParent(); lidPivot.add(lidMesh);
  lidMesh.position.set(0, 0, 0); lidMesh.quaternion.identity();
  lidPivot.position.y = LID_SEAT;
  lidOnCols = addLidColliders(toy, LID_SEAT);
  S.lidOn = true; S.snapping = null;
  updateLidButton();
}
function attachLidInstant() {
  if (S.lidOn) return;
  if (lidBody) { if (drag.body === lidBody) endDrag(); world.removeRigidBody(lidBody); lidBody = null; }
  putLidOnToy();
}

// Glide the free lid onto the basket, then lock it on. `arc` lifts it over first.
function snapLid(duration = 0.16, arc = 0) {
  if (S.lidOn || S.snapping || !lidBody) return;
  if (drag.body === lidBody) endDrag();
  S.snapping = { t: 0, duration, arc, fromPos: tv(lidBody.translation()), fromQ: tq(lidBody) };
  world.removeRigidBody(lidBody); lidBody = null;
  lidGroup.remove(lidMesh); scene.add(lidMesh);
}
function stepSnap(dt) {
  const s = S.snapping; if (!s) return;
  s.t += dt;
  const k = Math.min(s.t / s.duration, 1);
  const e = 1 - Math.pow(1 - k, 3);
  const toPos = toWorld(toy, new THREE.Vector3(0, LID_SEAT, 0));
  lidMesh.position.lerpVectors(s.fromPos, toPos, e);
  lidMesh.position.y += Math.sin(Math.PI * k) * s.arc;
  lidMesh.quaternion.slerpQuaternions(s.fromQ, tq(toy), e);
  if (k >= 1) {
    putLidOnToy();
    toy.wakeUp();
    toy.applyImpulse({ x: 0, y: -toy.mass() * 1.2, z: 0 }, true);
    U0.jigV.y -= 1;
    sfx.snap();
  }
}

// The lid clicks on when it's held (or was just let go) right over the basket,
// or when it has come to rest sitting square on the rim.
function checkAutoSnap() {
  if (!T.lid || S.lidOn || S.snapping || !lidBody || S.time < S.noSnapUntil) return;
  const up = upOf(toy); if (up.y < 0.6) return;
  const lidUp = upOf(lidBody);
  const align = lidUp.dot(up);
  if (align < 0.7) return;
  const d = tv(lidBody.translation()).sub(toWorld(toy, new THREE.Vector3(0, LID_SEAT, 0)));
  const along = d.dot(up);
  const lateral = d.clone().sub(up.clone().multiplyScalar(along)).length();
  const inHand = drag.body === lidBody || S.time - S.lidReleasedAt < 0.5;
  const speed = tv(lidBody.linvel()).length();
  const resting = speed < 1.5 && align > 0.9 && lateral < 0.5 && along < 0.9;
  if (along > -0.4 && ((inHand && lateral < 0.85 && along < 2.4) || resting)) snapLid(0.14);
}

// Roly-poly: if it ends up tipped over and has come to rest, it rolls itself back upright.
function stepSelfRight(dt) { for (const u of units) selfRight(u, dt); }
function selfRight(u, dt) {
  const toy = u.rb;
  const up = upOf(toy);
  const speed = tv(toy.linvel()).length();
  const spin = tv(toy.angvel()).length();
  const held = drag.body === toy || (u === U0 && drag.mode === 'lid');
  if (held || (u === U0 && S.snapping)) { u.tippedFor = 0; u.righting = false; return; }
  // done only once it's upright AND has stopped turning, so it can't sail past and flip again
  if (up.y > 0.985 && (!u.righting || spin < 0.8)) { u.tippedFor = 0; u.righting = false; return; }
  if (!u.righting) {
    // wait until it has really come to rest on its side, so tumbles play out naturally
    u.tippedFor = speed < 1.5 ? u.tippedFor + dt : Math.min(u.tippedFor, 0);
    if (u.tippedFor < 0.25) return;
    u.righting = true; u.rightT = 0;
  }
  // Steer its spin rather than push it: turn toward upright at a capped rate that eases off as
  // it arrives. A plain spring was too strong for wide, flat toys (the egg whipped straight
  // past upright and over onto its back again). If something blocks it, it gives up and waits.
  u.rightT += dt;
  if (u.rightT > 3.5) { u.righting = false; u.tippedFor = -2; return; }
  toy.wakeUp();
  const axis = up.clone().cross(new THREE.Vector3(0, 1, 0));
  const angle = Math.acos(THREE.MathUtils.clamp(up.y, -1, 1));
  if (axis.lengthSq() < 1e-6) axis.set(1, 0, 0); else axis.normalize();
  const ramp = Math.min(u.rightT / 0.25, 1);
  const want = axis.multiplyScalar(Math.min(4.5, 3.4 * angle) * ramp);
  const pi = toy.principalInertia();
  const I = (pi.x + pi.y + pi.z) / 3;
  const dw = want.sub(tv(toy.angvel())).multiplyScalar(I * Math.min(30 * dt, 1));
  toy.applyTorqueImpulse({ x: dw.x, y: dw.y, z: dw.z }, true);
  // a small lift off the edge it's lying on at the start, so it isn't fighting friction
  const m = toy.mass();
  if (angle > 0.5 && u.rightT < 0.45) toy.applyImpulse({ x: 0, y: m * G * 0.3 * ramp * dt, z: 0 }, true);
}

// A flexi that comes to rest on its back (or on its side) rolls itself back onto its belly
// after a moment: on screen, an upside-down flexi shows its open plate lattice and reads as
// broken. Pushing on its body isn't enough (the splayed legs prop it up like outriggers), so
// the whole animal does one deliberate move: a little hop and a roll about its own length,
// every piece carried together, and then the physics takes it back for the landing.
const fr = { still: 0, on: false, t: 0, h: 0, s: 1 };
function flexRight(dt) {
  const mid = units[flexCore[Math.floor(flexCore.length / 2)]]?.rb;
  if (!mid) return;
  if (drag.body) { fr.still = 0; fr.on = false; return; }
  const up = upOf(mid);
  if (!fr.on) {
    const fast = units.some((u) => tv(u.rb.linvel()).length() > 1.2);
    fr.still = fast || up.y > 0.3 ? 0 : fr.still + dt;
    if (fr.still < 0.6) return;
    fr.on = true; fr.t = 0; fr.still = 0;
  }
  fr.t += dt;
  // the roll axis: the animal's length, flat on the table; turn the way that brings its back up
  const len = new THREE.Vector3(0, 0, 1).applyQuaternion(tq(mid)); len.y = 0;
  if (len.lengthSq() < 1e-4) len.set(1, 0, 0);
  len.normalize();
  if (fr.t === dt) { const g = len.clone().cross(up).y; fr.s = g < -1e-3 ? -1 : 1; fr.h = 0; }
  const angle = Math.acos(THREE.MathUtils.clamp(up.y, -1, 1));
  if (up.y > 0.9 || fr.t > 2.2) { fr.on = false; fr.still = up.y > 0.9 ? 0 : -3; return; }
  // centre of mass of the whole animal
  const c = new THREE.Vector3(); let M = 0;
  for (const u of units) { const m = u.rb.mass(); c.addScaledVector(tv(u.rb.translation()), m); M += m; }
  c.divideScalar(M);
  if (fr.t === dt) fr.h = c.y + 1.6; // hop height: enough for the legs to clear the table
  const ease = Math.min(fr.t / 0.25, 1);
  const w = len.multiplyScalar(fr.s * Math.min(6, 3.2 * angle + 0.8) * ease);
  const vy = angle > 0.5 ? THREE.MathUtils.clamp((fr.h - c.y) * 7, -2, 6) * ease : -1;
  for (const u of units) {
    const b = u.rb; b.wakeUp();
    const r = tv(b.translation()).sub(c);
    const v = w.clone().cross(r); v.y += vy;
    b.setLinvel({ x: v.x, y: v.y, z: v.z }, true);
    b.setAngvel({ x: w.x, y: w.y, z: w.z }, true);
  }
}

// Rolling resistance. The physics has none, so a lid on its side would roll and rock for
// ages; real plastic on a desk loses its roll quickly once it's slow and down on the table.
function stepRolling(dt) {
  const bodies = units.map((u) => u.rb);
  if (lidBody) bodies.push(lidBody);
  for (const b of bodies) {
    if (b === drag.body || b.isSleeping()) continue;
    const v = b.linvel(), w = b.angvel();
    const speed = Math.hypot(v.x, v.y, v.z);
    if (speed > 3 || Math.abs(v.y) > 1.2) continue; // still flying or bouncing
    const kw = 1 - Math.min(2.2 * dt, 1), kv = 1 - Math.min(1.2 * dt, 1);
    b.setAngvel({ x: w.x * kw, y: w.y * kw, z: w.z * kw }, false);
    b.setLinvel({ x: v.x * kv, y: v.y, z: v.z * kv }, false);
  }
}

// ───────────────────────── pressing the dumpling ─────────────────────────
function stepPress(dt) { for (const b of buttons) stepButton(b, dt); }
function stepButton(B, dt) {
  const target = B.held ? 1 : 0;
  const k = B.held ? 1400 : 520, c = B.held ? 55 : 13;
  B.pressV += (k * (target - B.press) - c * B.pressV) * dt;
  B.press += B.pressV * dt;
  const v = B.pressV; // before the end stops eat it
  if (B.press > 1) { B.press = 1; B.pressV = 0; }
  if (B.press < -0.3) { B.press = -0.3; B.pressV = Math.abs(B.pressV) * 0.2; }
  // Blue switch timing: the click happens mid-travel, the bottom-out at the end,
  // and the jacket clicks again on the way back up.
  if (v > 0 && !B.clicked && B.press > 0.45) { B.clicked = true; sfx.blueDown(); registerClick(B); }
  if (v > 0 && !B.bottomed && B.press > 0.95) { B.bottomed = true; sfx.bottomOut(v / 8); }
  if (v < 0 && B.clicked && B.press < 0.55) { B.clicked = false; sfx.blueUp(); }
  if (v < 0 && B.bottomed && B.press < 0.06) { B.bottomed = false; sfx.topOut(); }
}
function registerClick(B) {
  const toy = B.unit.rb;
  S.clicks++;
  const now = performance.now();
  S.clickTimes.push(now);
  while (S.clickTimes.length && now - S.clickTimes[0] > 1000) S.clickTimes.shift();
  S.bestCps = Math.max(S.bestCps, S.clickTimes.length);
  ui.clicks.textContent = S.clicks;
  ui.cps.textContent = S.bestCps;
  // your finger pushes the whole toy into the desk a touch
  toy.applyImpulse(upOf(toy).multiplyScalar(-toy.mass() * 0.3), true);
  if (navigator.vibrate) navigator.vibrate(8);
}
function pressDown(B = activeBtn || buttons[0]) {
  if (!B || (B.unit === U0 && S.lidOn)) return;
  activeBtn = B; B.held = true; B.unit.rb.wakeUp();
}
function pressUp() { for (const b of buttons) b.held = false; }

// ───────────────────────── grabbing ─────────────────────────
// A springy hand, not a rigid pin: the grabbed point is pulled toward the cursor and the
// object's weight is carried, so it swings and lags a little like it's really held.
const drag = { mode: null, body: null, local: null, targetQ: null, plane: new THREE.Plane(), target: new THREE.Vector3(), smooth: new THREE.Vector3(), start: new THREE.Vector3(), px: 0, py: 0, moved: 0 };
const ray = new THREE.Raycaster();
const ndc = new THREE.Vector2();

function setRay(e) {
  const r = renderer.domElement.getBoundingClientRect();
  ndc.set(((e.clientX - r.left) / r.width) * 2 - 1, -((e.clientY - r.top) / r.height) * 2 + 1);
  ray.setFromCamera(ndc, camera);
}
function partOf(o) { while (o && !o.userData.part) o = o.parent; return o && o.userData.part; }
function findUp(o, key) { while (o && !o.userData[key]) o = o.parent; return o && o.userData[key]; }

// For flexis the hand is a kinematic body joined to the grabbed point: the joint solver then
// carries the whole animal's weight through its hinges (a force sized for one small segment
// couldn't lift a 26-part puppy off the table).
let hand = null;
function startGrab(body, point) {
  endGrab();
  drag.body = body;
  drag.local = toLocal(body, point);
  if (T.flexi) {
    if (!hand) hand = world.createRigidBody(RAPIER.RigidBodyDesc.kinematicPositionBased());
    hand.setTranslation(point, true);
    drag.handJoint = world.createImpulseJoint(RAPIER.JointData.spherical({ x: 0, y: 0, z: 0 }, drag.local), hand, body, true);
    drag.handJoint.setContactsEnabled(false);
  }
  // A pinch grip holds the angle it was picked up at. The lid swings upright so it can go back on.
  if (body === lidBody) {
    const fwd = new THREE.Vector3(0, 0, 1).applyQuaternion(tq(body));
    drag.targetQ = new THREE.Quaternion().setFromEuler(new THREE.Euler(0, Math.atan2(fwd.x, fwd.z), 0));
  } else drag.targetQ = tq(body);
  drag.smooth.copy(point);
  body.setAngularDamping(2.2); // a hand steadies what it holds; spin dies down instead of whipping
  body.wakeUp();
  drag.mode = 'grab';
}
function endGrab() {
  const b = drag.body;
  if (drag.handJoint) { world.removeImpulseJoint(drag.handJoint, true); drag.handJoint = null; }
  if (b) {
    const v = tv(b.linvel()); const sp = v.length();
    if (sp > 22) b.setLinvel(v.multiplyScalar(22 / sp), true);
    const w = tv(b.angvel()); const ws = w.length();
    if (ws > 9) b.setAngvel(w.multiplyScalar(9 / ws), true);
    if (sp > 6) S.thrown = true;
    // a flexi's other pieces carry speed too: let the whole animal go gently
    if (T.flexi && !fr.on) for (const u of units) {
      const uv = tv(u.rb.linvel()), us = uv.length();
      if (us > 8) u.rb.setLinvel(uv.multiplyScalar(8 / us), true);
    }
    if (b === lidBody) S.lidReleasedAt = S.time;
    if (T.flexi) b.setAngularDamping(FLEX_DAMP.ang); else b.setAngularDamping(0.12);
  }
  drag.body = null; drag.targetQ = null;
}
function endDrag() { endGrab(); drag.mode = null; stage.classList.remove('grabbing'); }

const _qe = new THREE.Quaternion();
function stepGrab(dt) {
  const b = drag.body; if (!b) return;
  const m = b.mass();
  // The pointer reports ~60 times a second and the physics runs at 120, so the raw target
  // moves in steps. Glide toward it instead, and keep it inside the room: a fast swipe near
  // the horizon would otherwise put the target miles away and yank the toy.
  const tgt = drag.target.clone();
  tgt.y = THREE.MathUtils.clamp(tgt.y, 0.3, 9);
  const hr = Math.hypot(tgt.x - view.target.x, tgt.z - view.target.z), maxR = S.arena + 1.5;
  if (hr > maxR) { tgt.x = view.target.x + (tgt.x - view.target.x) * maxR / hr; tgt.z = view.target.z + (tgt.z - view.target.z) * maxR / hr; }
  if (T.flexi) {
    // a flexi follows the hand gently: a softer glide and a top speed, so a quick flick of
    // the mouse swings it instead of cracking it like a whip
    const step = tgt.clone().sub(drag.smooth).multiplyScalar(1 - Math.exp(-dt * 6));
    const maxStep = 13 * dt;
    if (step.length() > maxStep) step.setLength(maxStep);
    drag.smooth.add(step);
  } else drag.smooth.lerp(tgt, 1 - Math.exp(-dt * 38));
  if (drag.handJoint) {
    hand.setNextKinematicTranslation(drag.smooth);
    // a light pinch: damp the held piece's spin so the animal swings rather than spins
    const w = tv(b.angvel()), pi = b.principalInertia(), I = (pi.x + pi.y + pi.z) / 3;
    const t = w.multiplyScalar(-I * 6 * dt);
    b.applyTorqueImpulse({ x: t.x, y: t.y, z: t.z }, true);
    return;
  }
  const p = toWorld(b, drag.local);
  const v = velAt(b, p);
  const K = 420, C = 2 * Math.sqrt(K) * 0.95; // just under critical: follows snappily without ringing
  const f = drag.smooth.clone().sub(p).multiplyScalar(K).sub(v.multiplyScalar(C)).multiplyScalar(m);
  f.y += m * G; // carry its weight
  const cap = m * G * 5;
  if (f.length() > cap) f.setLength(cap);
  // Mostly through the centre of mass (a firm grip moves the whole thing), a little at the
  // grabbed point so it still swings a touch like it's really in your fingers.
  f.multiplyScalar(dt);
  impulseAt(b, f.clone().multiplyScalar(0.75), tv(b.worldCom()));
  impulseAt(b, f.multiplyScalar(0.25), p);
  // grip: a soft rotational spring toward the held angle
  _qe.copy(drag.targetQ).multiply(tq(b).invert());
  if (_qe.w < 0) _qe.set(-_qe.x, -_qe.y, -_qe.z, -_qe.w);
  const angle = 2 * Math.acos(Math.min(_qe.w, 1));
  const s = Math.sqrt(Math.max(1 - _qe.w * _qe.w, 0));
  const axis = s > 1e-5 ? new THREE.Vector3(_qe.x / s, _qe.y / s, _qe.z / s) : new THREE.Vector3();
  const I = m * (b === toy ? 1.7 : 0.9);
  const KR = 150, DR = 2 * Math.sqrt(KR);
  const w = tv(b.angvel());
  const t = axis.multiplyScalar(KR * angle).sub(w.multiplyScalar(DR)).multiplyScalar(I * dt);
  b.applyTorqueImpulse({ x: t.x, y: t.y, z: t.z }, true);
}

// While the lid is still on: it stretches against the seal and the basket leans into the pull.
function stepLidPull(dt) {
  if (drag.mode !== 'lid') return;
  const up = upOf(toy);
  const grabWorld = toWorld(toy, drag.local);
  const along = drag.target.clone().sub(drag.start).dot(up);
  S.lidLift = along > 0 ? 0.3 * (1 - Math.exp(-along / 0.8)) : 0;
  const m = toy.mass();
  const f = drag.target.clone().sub(grabWorld).multiplyScalar(18 * m);
  const cap = m * G * 0.8; // under its weight, so it leans rather than lifts
  if (f.length() > cap) f.setLength(cap);
  impulseAt(toy, f.multiplyScalar(dt), grabWorld);
  if (along > 1.35) {
    const hit = grabWorld.clone().add(up.clone().multiplyScalar(S.lidLift));
    detachLid(up.clone().multiplyScalar(5));
    startGrab(lidBody, hit);
  }
}

stage.addEventListener('pointerdown', (e) => {
  audio();
  try { stage.setPointerCapture(e.pointerId); } catch {}
  setRay(e);
  drag.px = e.clientX; drag.py = e.clientY; drag.moved = 0;
  const hit = ray.intersectObjects([...units.map((u) => u.group), lidGroup, lidMesh].filter(Boolean), true)[0];
  const part = hit && partOf(hit.object);
  stage.classList.add('grabbing');
  if (!hit) { drag.mode = 'orbit'; return; }
  drag.plane.setFromNormalAndCoplanarPoint(camera.getWorldDirection(new THREE.Vector3()).negate(), hit.point);
  drag.target.copy(hit.point); drag.start.copy(hit.point);
  const btn = findUp(hit.object, 'btn'), unit = findUp(hit.object, 'unit') || U0;
  if (part === 'blade') { drag.mode = 'pending'; drag.hitPoint = hit.point.clone(); drag.y0 = e.clientY; drag.x0 = e.clientX; drag.e0 = spiral.target; return; }
  if (part === 'button' && btn && !(btn.unit === U0 && S.lidOn)) { drag.mode = 'press'; drag.hitPoint = hit.point.clone(); drag.unit = btn.unit; pressDown(btn); return; }
  if (part === 'lid' && S.lidOn) { drag.mode = 'lid'; drag.local = toLocal(toy, hit.point); toy.wakeUp(); return; }
  if (part === 'lid' && lidBody) { startGrab(lidBody, hit.point); return; }
  startGrab(unit.rb, hit.point);
});
stage.addEventListener('pointermove', (e) => {
  if (!drag.mode) return;
  drag.moved = Math.max(drag.moved, Math.hypot(e.clientX - drag.px, e.clientY - drag.py));
  if (drag.mode === 'orbit') {
    view.az -= (e.movementX || 0) * 0.006;
    view.pol = THREE.MathUtils.clamp(view.pol - (e.movementY || 0) * 0.004, 0.55, 1.35);
    placeCamera(); computeArena();
    return;
  }
  setRay(e);
  ray.ray.intersectPlane(drag.plane, drag.target);
  if (drag.mode === 'press' && drag.moved > 8) { pressUp(); startGrab(drag.unit.rb, drag.hitPoint); }
  if (drag.mode === 'pending' && drag.moved > 6) {
    const dx = Math.abs(e.clientX - drag.x0), dy = Math.abs(e.clientY - drag.y0);
    // up/down twists it; sideways picks it up (only when it's shut: an open tower holds its shape)
    if (dy > dx * 1.1 || spiral.target > 0.04) { drag.mode = 'twist'; spiral.anim = null; }
    else startGrab(U0.rb, drag.hitPoint);
  }
  if (drag.mode === 'twist') {
    spiral.target = THREE.MathUtils.clamp(drag.e0 + (drag.y0 - e.clientY) / (stage.clientHeight * 0.5), 0, 1);
    updateTwistButton();
  }
});
function release() {
  if (drag.mode === 'press') pressUp();
  endDrag();
}
stage.addEventListener('pointerup', release);
stage.addEventListener('pointercancel', release);
stage.addEventListener('wheel', (e) => {
  e.preventDefault();
  view.zoom = THREE.MathUtils.clamp(view.zoom * (1 + e.deltaY * 0.0012), 0.42, 1.45);
  placeCamera(); computeArena();
}, { passive: false });

// ───────────────────────── arena: keep toys on screen ─────────────────────────
const floor = new THREE.Plane(new THREE.Vector3(0, 1, 0), 0);
function computeArena() {
  const pts = [[0.86, 0], [-0.86, 0], [0, -0.8], [0, 0.55]];
  let r = 20;
  const p = new THREE.Vector3();
  for (const [x, y] of pts) {
    ray.setFromCamera(new THREE.Vector2(x, y), camera);
    if (ray.ray.intersectPlane(floor, p)) r = Math.min(r, Math.hypot(p.x - view.target.x, p.z - view.target.z));
  }
  // the arena is set by the unzoomed view so zooming in doesn't herd toys together
  S.arena = Math.max(2.5, r / view.zoom - 1.6);
}
const restFar = new Map();
function leash(b, dt) {
  const t = b.translation();
  const dx = t.x - view.target.x, dz = t.z - view.target.z;
  const r = Math.hypot(dx, dz);
  const v = b.linvel();
  // Only steer things that are flying or moving fast. Pushing on a toy that's resting or
  // rolling slowly past the edge just fights friction (it twitches) or yanks it back with a
  // visible jolt; slow rollers are stopped by the rolling resistance instead. The arena keeps
  // a margin, so anything that stops just past it is still on screen.
  const speed = Math.hypot(v.x, v.y, v.z);
  if (r > S.arena && (speed > 2.5 || Math.abs(v.y) > 1.5) && !b.isSleeping()) {
    const k = Math.min(r - S.arena, 3) * 22 * b.mass() * dt;
    b.applyImpulse({ x: (-dx / r) * k, y: 0, z: (-dz / r) * k }, true);
    const out = (v.x * dx + v.z * dz) / r;
    const brake = r > S.arena + 1 ? 0.3 : 0.12; // the further out, the harder it's slowed
    if (out > 0) b.setLinvel({ x: v.x - (dx / r) * out * brake, y: v.y, z: v.z - (dz / r) * out * brake }, true);
  }
  // Came to rest out of view: after a beat, hop it back toward the middle in one clean arc
  // (a deliberate move reads fine; constant nudging is what looked like twitching).
  const far = r > S.arena + 1.5 && speed < 0.3 && b !== drag.body;
  const waited = far ? (restFar.get(b.handle) || 0) + dt : 0;
  restFar.set(b.handle, waited);
  if (waited > 0.6) {
    restFar.set(b.handle, 0);
    const vy = 12, T = (2 * vy) / G, d = r - Math.max(S.arena - 1.2, 1);
    b.wakeUp();
    b.setLinvel({ x: (-dx / r) * (d / T), y: vy, z: (-dz / r) * (d / T) }, true);
  }
  if (t.y > 13 && v.y > 0) b.setLinvel({ x: v.x, y: v.y * 0.5, z: v.z }, true);
  if (t.y < -3) { b.setTranslation({ x: 0, y: 6, z: 0 }, true); b.setLinvel({ x: 0, y: 0, z: 0 }, true); }
}

// ───────────────────────── UI ─────────────────────────
const ui = {
  clicks: document.getElementById('sClicks'), cps: document.getElementById('sCps'),
  lid: document.getElementById('bLid'), drop: document.getElementById('bDrop'), click: document.getElementById('bClick'), sound: document.getElementById('bSound'),
  buyText: document.getElementById('buyText'), colorName: document.getElementById('colorName'), swatches: document.getElementById('swatches'), hint: document.getElementById('hint'),
};
document.getElementById('toyTitle').innerHTML = T.title;
document.getElementById('toyLede').textContent = T.lede;
document.getElementById('howTo').textContent = T.how;
document.getElementById('loadText').textContent = T.loading;
document.getElementById('credit').innerHTML = `Design by ${CREDIT[T.credit]} · printed by PLA Toys`;
document.getElementById('buy').href = `${SHOP}/products/${T.handle}`;
const detailsLink = document.getElementById('details');
if (detailsLink) detailsLink.href = `${SHOP}/products/${T.handle}`;
document.getElementById('specKind').textContent = T.flexi ? 'Flexi' : T.spiral ? 'Spiral' : 'Clicker';
stage.setAttribute('aria-label', `Interactive 3D ${T.short} Clicker`);
if (!EMBED) document.title = `Playground · ${T.short} · PLA Toys`;
const nav = document.getElementById('toyNav');
for (const [key, t] of Object.entries(TOYS)) {
  const a = document.createElement('a');
  const q = new URLSearchParams(location.search); q.set('toy', key);
  a.href = `?${q}`; a.innerHTML = `<span class="n">${t.no}</span>${t.short}`;
  if (t === T) a.setAttribute('aria-current', 'page');
  nav.appendChild(a);
}
// on a phone the list scrolls sideways: bring the current toy into view
if (!EMBED) nav.querySelector('[aria-current]')?.scrollIntoView({ block: 'nearest', inline: 'center' });
if (!COLORS.length) document.getElementById('colorRow').hidden = true;
if (!T.lid) ui.lid.hidden = true;
if (T.flexi || T.spiral) { ui.click.hidden = true; ui.clicks.closest('.row').hidden = true; }
// On the store the buy button adds the exact variant you're playing with to the cart (and
// opens the cart drawer); anywhere else it links to the product page.
const product = PRODUCTS[T.handle];
const variantFor = (name) => {
  if (!product) return null;
  const v = product.variants || {};
  return v[name] ?? v['Default Title'] ?? Object.values(v)[0] ?? null;
};
const canAdd = () => !!(product && window.PLA && window.PLA.addItems);
const setBuy = (name) => {
  ui.buyText.textContent = canAdd() ? `Add ${COLORS.length ? name + ' ' : ''}to cart · ${T.price}` : `${T.buy(name)} · ${T.price}`;
};
// theme.js (which defines window.PLA) can load after this script; relabel once it has
if (product && !window.PLA) addEventListener('load', () => setBuy(ui.colorName.textContent));
document.getElementById('buy').addEventListener('click', (e) => {
  if (!canAdd()) return;
  const id = variantFor(ui.colorName.textContent || 'Default Title');
  if (!id) return;
  e.preventDefault();
  window.PLA.addItems([{ id, quantity: 1 }]);
  // a beat of confirmation on the button itself (the cart drawer opens too)
  const name = ui.colorName.textContent;
  ui.buyText.textContent = 'Added to your cart ✓';
  document.getElementById('buy').classList.add('added');
  setTimeout(() => { setBuy(name); document.getElementById('buy').classList.remove('added'); }, 1800);
});
// A product page's 3D view follows the colour picked on the page around it: the page fires
// a 'pla-play-color' event on document with the variant's name.
document.addEventListener('pla-play-color', (e) => {
  const want = String(e.detail || '').toLowerCase();
  const sw = [...ui.swatches.querySelectorAll('.sw')].find((b) => want.includes(b.getAttribute('aria-label').toLowerCase()));
  if (sw && sw.getAttribute('aria-checked') !== 'true') sw.click();
});

// Once someone has really played with the toy (a run of clicks, the lid off, a throw, a
// twist, a good swing of a flexi), the panel says so once: a short line over the buy
// button and one soft pulse. Nothing pops up over the toy itself.
const nudge = { done: EMBED, held: 0 };
function checkNudge(dt) {
  if (nudge.done) return;
  if (drag.body) nudge.held += dt;
  const played = S.clicks >= 8 || (T.lid && !S.lidOn && S.time > 3) || S.thrown || S.twisted || nudge.held > 4;
  if (!played) return;
  nudge.done = true;
  setTimeout(() => {
    const el = document.getElementById('nudge');
    if (!el) return;
    const name = COLORS.length ? ui.colorName.textContent.toLowerCase() : '';
    el.textContent = name ? `Like how it feels? Yours gets printed fresh, in ${name}.` : 'Like how it feels? Yours gets printed fresh for you.';
    el.hidden = false;
    requestAnimationFrame(() => el.classList.add('on'));
    document.getElementById('buy').classList.add('pulse');
  }, 900);
}
setBuy(COLORS[0]?.[0]);
if (COLORS[0]) ui.colorName.textContent = COLORS[0][0];
let colorTarget = new THREE.Color(COLORS[0]?.[1] || '#ffffff');
COLORS.forEach(([name, hex], i) => {
  const b = document.createElement('button');
  b.className = 'sw'; b.type = 'button'; b.style.background = hex;
  b.setAttribute('role', 'radio'); b.setAttribute('aria-label', name); b.setAttribute('aria-checked', i === 0 ? 'true' : 'false');
  b.addEventListener('click', () => {
    ui.swatches.querySelectorAll('.sw').forEach((s) => s.setAttribute('aria-checked', 'false'));
    b.setAttribute('aria-checked', 'true');
    colorTarget = new THREE.Color(hex);
    ui.colorName.textContent = name;
    setBuy(name);
    U0.jigV.y += 0.8;
  });
  ui.swatches.appendChild(b);
});
// tell the page around us that the colour can be set now
if (EMBED) document.dispatchEvent(new CustomEvent('pla-play-ready'));
function updateLidButton() { ui.lid.textContent = S.lidOn ? 'Lid off' : 'Lid on'; }
ui.lid.addEventListener('click', () => {
  audio();
  if (T.spiral) { twistTo(spiral.target > 0.5 ? 0 : 1); return; }
  if (S.lidOn) {
    // straight up first so it clears the rim, with a little drift and spin
    const up = upOf(toy);
    detachLid(up.multiplyScalar(15));
    // once it has cleared the rim, send it off to the side so it lands beside the basket
    const side = new THREE.Vector3(Math.random() - 0.5, 0, Math.random() - 0.5).setLength(6);
    S.lidKick = { at: S.time + 0.09, v: side };
  } else snapLid(0.5, 2.2);
});
ui.drop.addEventListener('click', () => { audio(); spawn(); });
ui.click.addEventListener('pointerdown', () => { audio(); if (T.lid && S.lidOn) ui.lid.click(); pressDown(); });
ui.click.addEventListener('pointerup', pressUp);
ui.click.addEventListener('pointerleave', pressUp);
ui.sound.addEventListener('click', () => {
  S.sound = !S.sound;
  ui.sound.textContent = S.sound ? 'Sound on' : 'Sound off';
  ui.sound.setAttribute('aria-pressed', String(!S.sound));
});
addEventListener('keydown', (e) => {
  if (EMBED) return; // inside a store page the space bar still scrolls the page
  if (e.code !== 'Space' || e.repeat || e.target.closest?.('button,a,input')) return;
  e.preventDefault(); audio();
  if (T.spiral) { twistTo(spiral.target > 0.5 ? 0 : 1); return; }
  pressDown();
});
addEventListener('keyup', (e) => { if (!EMBED && e.code === 'Space') pressUp(); });

const hintEl = ui.hint;
function stepHint() {
  let text = '', anchor = null;
  if (T.lid && S.lidOn && !S.popped) { text = 'Pull the lid up ↑'; anchor = new THREE.Vector3(0, LID_SEAT + LID_H + 0.3, 0).applyMatrix4(toyGroup.matrixWorld); }
  else if (!S.lidOn && S.clicks === 0 && buttons[0]) { text = T.pressHint; anchor = new THREE.Vector3(0, BTN_TOP + 0.25, 0).applyMatrix4(buttons[0].pivot.matrixWorld); }
  else if (S.clicks > 0 && S.clicks < 6 && !S.thrown && buttons[0]) { text = 'Now grab it and throw it'; anchor = new THREE.Vector3(0, BTN_TOP + 0.25, 0).applyMatrix4(buttons[0].pivot.matrixWorld); }
  if (T.spiral && spiral.e < 0.05 && !S.twisted) { text = 'Drag up to twist it open ↑'; anchor = new THREE.Vector3(0, spiral.height + 0.4, 0).applyMatrix4(toyGroup.matrixWorld); }
  if (T.spiral && spiral.e > 0.3) S.twisted = true;
  if (T.flexi && !S.thrown && units[0]) { text = 'Grab it and give it a swing'; anchor = new THREE.Vector3(0, 1.2, 0).applyMatrix4(units[0].group.matrixWorld); }
  if (!text || drag.mode) { hintEl.classList.remove('on'); return; }
  hintEl.textContent = text;
  anchor.project(camera);
  const r = renderer.domElement.getBoundingClientRect(), box = ROOT.getBoundingClientRect();
  hintEl.style.left = `${r.left - box.left + ((anchor.x + 1) / 2) * r.width}px`;
  hintEl.style.top = `${r.top - box.top + ((1 - anchor.y) / 2) * r.height - 8}px`;
  hintEl.classList.add('on');
}

// ───────────────────────── resize ─────────────────────────
function resize() {
  const w = stage.clientWidth, h = stage.clientHeight;
  if (!w || !h) return;
  renderer.setSize(w, h, false);
  camera.aspect = w / h;
  view.dist = (w / h < 0.9 ? 56 : 42) * (T.viewScale || 1); // phones: pull back so the arena still fits
  camera.updateProjectionMatrix();
  placeCamera(); computeArena();
}
new ResizeObserver(resize).observe(stage);
resize();

// ───────────────────────── 3D-printed look ─────────────────────────
// Layer lines are shaded in, not modelled: each part's local Y is its print Z, so every
// LAYER of height gets a rounded bead (normal tilts up at its top edge, down at its bottom)
// and a dark seam between beads. Fades out when a layer gets thinner than a pixel, so it
// never shimmers; zoom in and the lines get crisper.
const LAYER = 0.04; // 0.4 mm: a little coarser than a real 0.2 mm print so it reads on screen
function printLines(mat) {
  mat.onBeforeCompile = (sh) => {
    sh.uniforms.uLayer = { value: LAYER };
    sh.vertexShader = sh.vertexShader
      .replace('#include <common>', '#include <common>\nvarying float vPrintY;\nvarying vec3 vPrintUp;')
      .replace('#include <begin_vertex>', '#include <begin_vertex>\nvPrintY = position.y;\nvPrintUp = normalize(normalMatrix * vec3(0.0, 1.0, 0.0));');
    sh.fragmentShader = sh.fragmentShader
      .replace('#include <common>', '#include <common>\nuniform float uLayer;\nvarying float vPrintY;\nvarying vec3 vPrintUp;')
      .replace('#include <normal_fragment_maps>', `#include <normal_fragment_maps>
        {
          float ly = vPrintY / uLayer;
          float vis = 1.0 - smoothstep(0.3, 0.75, fwidth(ly));
          float f = fract(ly) * 2.0 - 1.0;            // -1 bottom of a bead, +1 top
          vec3 up = normalize(vPrintUp);
          vec3 along = up - normal * dot(normal, up);  // the print axis, flattened onto the surface
          float wall = length(along);                 // 0 on flat tops, where there are no layer lines
          float onWall = smoothstep(0.02, 0.25, wall);
          if (wall > 0.02) {
            float bead = f * abs(f);                  // round in the middle, steep at the seams
            normal = normalize(normal + (along / wall) * bead * 0.75 * vis * onWall);
          }
          float seam = smoothstep(0.6, 1.0, abs(f)) * onWall;
          diffuseColor.rgb *= 1.0 - seam * 0.16 * vis;
        }`);
  };
  mat.customProgramCacheKey = () => 'print-lines';
}

// ───────────────────────── load ─────────────────────────
new GLTFLoader().load(ASSETS + T.model, (gltf) => {
  // A multi-material node can come out of the loader as two siblings ("dumpling" holding the
  // extras, "dumpling_1" holding the meshes), so gather everything sharing a base name.
  const base = (o) => o.name.replace(/_\d+$/, '');
  const get = (n) => {
    const list = gltf.scene.children.filter((o) => base(o) === n);
    if (!list.length) return null;
    const g = new THREE.Group(); g.name = n;
    list.forEach((o) => Object.assign(g.userData, o.userData));
    list.forEach((o) => g.add(o));
    return g;
  };
  if (T.multi) { loadMulti(gltf); return; }
  if (T.flexi) { loadFlexi(gltf); return; }
  if (T.spiral) { loadSpiral(gltf); return; }
  btnMesh = get(T.button); lidMesh = T.lid ? get(T.lid) : null;
  const bodyParts = [...gltf.scene.children];
  if (lidMesh) LID_SEAT = lidMesh.userData.seatY ?? LID_SEAT;
  BTN_SEAT = btnMesh.userData.seatY ?? BTN_SEAT;
  BTN_TOP = new THREE.Box3().setFromObject(btnMesh).max.y - btnMesh.position.y;
  const parts = [...bodyParts.map((o) => [o, 'body']), [btnMesh, 'button']];
  if (lidMesh) parts.push([lidMesh, 'lid']);
  for (const [o, part] of parts) {
    o.position.set(0, 0, 0);
    o.userData.part = part;
    o.traverse((c) => {
      if (!c.isMesh) return;
      c.castShadow = true; c.receiveShadow = true;
      if (T.recolor.includes(c.material.name)) { c.material = c.material.clone(); bodyMats.push(c.material); }
      c.material.roughness = c.material.name === 'body' && T.buttonRough ? T.buttonRough : Math.min(c.material.roughness, 0.6); // PLA has a soft sheen
      printLines(c.material);
    });
  }
  bodyMats.forEach((m) => m.color.copy(colorTarget));
  bodyParts.forEach((o) => toyGroup.add(o));
  btnPivot.add(btnMesh);
  makeButton(U0, btnPivot, new THREE.Vector3(0, BTN_SEAT, 0));
  ballast(toy, null);
  squashers = T.squashParts ? bodyParts.filter((o) => T.squashParts.includes(o.name.replace(/_\d+$/, ''))) : [];
  if (lidMesh) { lidPivot.add(lidMesh); lidPivot.position.y = LID_SEAT; }

  // Body colliders: the dumpling's basket needs real cylinders so the lid slides over its rim;
  // anything else is the convex hull of its static parts.
  if (T.colliders === 'basket') basketColliders();
  else {
    const hp = [];
    toyGroup.updateMatrixWorld(true);
    for (const o of bodyParts) o.traverse((c) => {
      if (!c.isMesh) return;
      const a = c.geometry.attributes.position;
      for (let i = 0; i < a.count; i += 4) hp.push(a.getX(i), a.getY(i), a.getZ(i));
    });
    col(RAPIER.ColliderDesc.convexHull(new Float32Array(hp)), toy);
  }

  // The bun's collider is the convex hull of its real mesh.
  const pts = [];
  btnMesh.traverse((c) => {
    if (!c.isMesh) return;
    const a = c.geometry.attributes.position;
    for (let i = 0; i < a.count; i += 5) pts.push(a.getX(i), a.getY(i) + BTN_SEAT, a.getZ(i));
  });
  col(RAPIER.ColliderDesc.convexHull(new Float32Array(pts)), toy);
  if (T.lid) lidOnCols = addLidColliders(toy, LID_SEAT);
  start();
});

function styleMeshes(o) {
  o.traverse((c) => {
    if (!c.isMesh) return;
    c.castShadow = true; c.receiveShadow = true;
    c.material.roughness = Math.min(c.material.roughness, 0.6);
    printLines(c.material);
  });
}
function hullOf(objs, offset = new THREE.Vector3(), every = 4) {
  const pts = [];
  for (const o of objs) o.traverse((c) => {
    if (!c.isMesh) return;
    const a = c.geometry.attributes.position;
    for (let i = 0; i < a.count; i += every) pts.push(a.getX(i) + offset.x, a.getY(i) + offset.y, a.getZ(i) + offset.z);
  });
  return RAPIER.ColliderDesc.convexHull(new Float32Array(pts));
}

// Several toys in one GLB: a rigid body per toy, a pressable pivot per button node.
function loadMulti(gltf) {
  const nodes = [...gltf.scene.children];
  const byToy = new Map();
  for (const o of nodes) {
    const t = o.userData.toy ?? 0;
    if (!byToy.has(t)) byToy.set(t, []);
    byToy.get(t).push(o);
  }
  for (const [t, list] of [...byToy.entries()].sort((a, b) => a[0] - b[0])) {
    let rb, group;
    if (t === 0) { rb = toy; group = toyGroup; }
    else {
      rb = world.createRigidBody(RAPIER.RigidBodyDesc.dynamic().setTranslation(0, 7, 0).setLinearDamping(0.05).setAngularDamping(0.12).setCcdEnabled(true));
      group = new THREE.Group(); scene.add(group);
    }
    const unit = t === 0 ? U0 : makeUnit(rb, group);
    const bodies = list.filter((o) => o.userData.role !== 'button');
    bodies.forEach((o) => { o.position.set(0, 0, 0); o.userData.part = 'body'; styleMeshes(o); group.add(o); });
    col(hullOf(bodies), rb);
    group.updateMatrixWorld(true);
    ballast(rb, new THREE.Box3().setFromObject(group));
    for (const o of list.filter((x) => x.userData.role === 'button')) {
      const pivot = new THREE.Group();
      group.add(pivot);
      o.position.set(0, 0, 0); styleMeshes(o); pivot.add(o);
      const seat = new THREE.Vector3(...o.userData.seat);
      makeButton(unit, pivot, seat);
      col(hullOf([o], seat, 3), rb);
      BTN_TOP = new THREE.Box3().setFromObject(o).max.y;
    }
  }
  start();
}

// Flexi: a body per bone, hinged where the segments interlock.
// The animal's own parts never collide with each other: interlocked segments overlap at
// their hinges, and the springs and limits stand in for those contacts.
const flexJoints = [];
// The animal's pieces bump into each other, so a flipper can't swing through the body,
// except pairs that already touch in the printed pose: interlocked neighbours sit inside
// each other's hulls, and letting those collide would blow the animal apart.
const FLEX_DAMP = { lin: 0.5, ang: 1.05 };
let flexCore = []; // unit indices of the body (not the limbs)
const flexColliders = []; // [collider, bone index]
const flexSkip = new Set();
const pairKey = (a, b) => (a < b ? `${a},${b}` : `${b},${a}`);
const flexHooks = {
  filterContactPair: (c1, c2) => (flexSkip.has(pairKey(c1, c2)) ? null : RAPIER.SolverFlags.COMPUTE_IMPULSE),
  filterIntersectionPair: () => true,
};
let flexRest = [];
// Flexis get a little stage like their product shots: pastel podiums at different heights,
// all solid, so an animal can lie draped over an edge or hang off a step. It
// lands on the tall lavender one in the middle.
const STAGE = [
  { x: 0, z: 0, r: 4.3, h: 2.0, color: '#d9ccfb' },      // lavender
  { x: 6.0, z: 2.6, r: 2.8, h: 1.2, color: '#bdeedb' },  // mint, a step down
  { x: -5.4, z: 3.2, r: 2.4, h: 0.35, color: '#ffffff' }, // low white
];
const STAGE_TOP = T.flexi ? STAGE[0].h : 0;
function addStage() {
  for (const p of STAGE) {
    const mat = new THREE.MeshStandardMaterial({ color: p.color, roughness: 0.75, metalness: 0 });
    const mesh = p.ball
      ? new THREE.Mesh(new THREE.SphereGeometry(p.ball, 48, 32), mat)
      : new THREE.Mesh(new THREE.CylinderGeometry(p.r, p.r, p.h, 96), mat);
    mesh.position.set(p.x, p.ball ? p.ball : p.h / 2, p.z);
    mesh.castShadow = true; mesh.receiveShadow = true;
    scene.add(mesh);
    // fixed, and part of the desk, so knocks on them sound like knocks on the table
    const desc = p.ball ? RAPIER.ColliderDesc.ball(p.ball) : RAPIER.ColliderDesc.cylinder(p.h / 2, p.r);
    world.createCollider(desc.setTranslation(p.x, p.ball ? p.ball : p.h / 2, p.z).setFriction(0.7).setRestitution(0.15), deskBody);
  }
  view.target.y += STAGE_TOP * 0.6;
}

function loadFlexi(gltf) {
  const nodes = [...gltf.scene.children];
  const joints = gltf.scene.userData.joints || [];
  const kinds = nodes.map((o) => o.userData.kind);
  flexCore = kinds.map((k, i) => (k === 'core' ? i : -1)).filter((i) => i >= 0);
  // lowest point of the animal -> sits on the table
  const box = new THREE.Box3().setFromObject(gltf.scene);
  const lift = -box.min.y;
  const center = box.getCenter(new THREE.Vector3());
  nodes.forEach((o, i) => {
    const rest = o.position.clone(); rest.y += lift; rest.x -= center.x; rest.z -= center.z;
    let rb;
    if (i === 0) rb = toy;
    else rb = world.createRigidBody(RAPIER.RigidBodyDesc.dynamic().setCcdEnabled(true));
    // thick, slow air: swings glide and die away softly
    rb.setLinearDamping(FLEX_DAMP.lin); rb.setAngularDamping(FLEX_DAMP.ang);
    rb.setTranslation(rest, true);
    const group = i === 0 ? toyGroup : new THREE.Group();
    if (i !== 0) scene.add(group);
    o.position.set(0, 0, 0);
    o.userData.part = 'body';
    styleMeshes(o);
    group.add(o);
    const u = i === 0 ? U0 : makeUnit(rb, group);
    // one hull per printed piece; they touch the other bones' pieces (see flexSkip below)
    for (const pts of o.userData.hulls || []) {
      const desc = RAPIER.ColliderDesc.convexHull(new Float32Array(pts));
      if (!desc) continue;
      desc.setActiveHooks(RAPIER.ActiveHooks.FILTER_CONTACT_PAIRS);
      flexColliders.push([col(desc, rb, { friction: 0.6 }), i]);
    }
    flexRest.push(rest);
  });
  for (let x = 0; x < flexColliders.length; x++) for (let y = x + 1; y < flexColliders.length; y++) {
    const [ca, ba] = flexColliders[x], [cb, bb] = flexColliders[y];
    if (ba === bb) continue;
    if (ca.shape.contactShape(ca.translation(), ca.rotation(), cb.shape, cb.translation(), cb.rotation(), 0.05)) flexSkip.add(pairKey(ca.handle, cb.handle));
  }
  // Even out the masses: a toe weighing a hundredth of a body plate makes a joint chain
  // unstable, so every segment gets at least a fair share of the animal's weight.
  const total = units.reduce((m, u) => m + u.rb.mass(), 0);
  const floor = total / units.length * 0.6;
  for (const u of units) if (u.rb.mass() < floor) u.rb.setAdditionalMass(floor - u.rb.mass(), true);
  world.numSolverIterations = 24;
  world.timestep = 1 / 240; // finer steps keep a whipped joint chain tight
  world.integrationParameters.numInternalPgsIterations = 4; // and so do extra passes over the joints
  for (const j of joints) {
    const ua = units[j.a], ub = units[j.b];
    if (!ua || !ub) continue;
    const at = new THREE.Vector3(j.at[0] - center.x, j.at[1] + lift, j.at[2] - center.z);
    const la = at.clone().sub(flexRest[j.a]), lb = at.clone().sub(flexRest[j.b]);
    // The interlocked plates let each joint bend any way, up and down as much as side to
    // side, until the plates butt against each other, but they can't twist: lock the twist
    // about the line between the two pieces in the solver, and leave the bend and its stop
    // to stepFlexJoints.
    const axis = flexRest[j.b].clone().sub(flexRest[j.a]);
    if (axis.lengthSq() < 1e-6) axis.set(0, 0, 1);
    axis.normalize();
    const M = RAPIER.JointAxesMask;
    const joint = world.createImpulseJoint(RAPIER.JointData.generic(la, lb, axis, M.LinX | M.LinY | M.LinZ | M.AngX), ua.rb, ub.rb, true);
    // neighbouring pieces still butt into each other where they don't already touch (flexSkip)
    // Stops: a body joint bends up and down (about its sideways X) further than it bends
    // side to side (about Y), where the plates jam sooner. Limbs bend the same both ways.
    const limb = kinds[j.a] === 'outer' || kinds[j.b] === 'outer';
    const lim = limb ? (T.limbBend ?? 0.6) : (T.bend ?? 0.5);
    const limY = limb ? lim : (T.sideBend ?? lim);
    // Stops and friction live in the solver, per swing axis of the joint's frame (its X is
    // the twist axis; whichever of its Y and Z stands upright is the side-to-side swing).
    // Raw axis numbers: 4 = AngY, 5 = AngZ. The friction is a motor holding zero speed with
    // limited strength: it holds a pose you bend it into, and gives when you swing it.
    const fr = new THREE.Quaternion().copy(joint.frameX1());
    const fy = new THREE.Vector3(0, 1, 0).applyQuaternion(fr), fz = new THREE.Vector3(0, 0, 1).applyQuaternion(fr);
    const upright = Math.abs(fy.y) > Math.abs(fz.y) ? 4 : 5;
    const fric = (limb ? (T.limbFriction ?? 0.5) : 0.5) * Math.min(ua.rb.mass(), ub.rb.mass());
    for (const ax of [4, 5]) {
      const l = ax === upright ? limY : lim;
      joint.rawSet.jointSetLimits(joint.handle, ax, -l, l);
      joint.rawSet.jointConfigureMotorVelocity(joint.handle, ax, 0, fric);
    }
    flexJoints.push({ a: ua.rb, b: ub.rb, joint, ia: j.a, ib: j.b, lim, limY });
  }
  // How much each joint may have to hold up: the heavier of the two halves it splits the
  // animal into, in units of its own lighter piece (capped so the stop stays stable).
  for (const fj of flexJoints) {
    const seen = new Set([fj.ia]), stack = [fj.ib]; seen.add(fj.ib);
    let mb = 0;
    while (stack.length) {
      const k = stack.pop(); mb += units[k].rb.mass();
      for (const o of flexJoints) for (const [x, y] of [[o.ia, o.ib], [o.ib, o.ia]]) if (x === k && !seen.has(y)) { seen.add(y); stack.push(y); }
    }
    const ma = units.reduce((m, u) => m + u.rb.mass(), 0) - mb;
    fj.load = THREE.MathUtils.clamp(Math.max(ma, mb) / Math.min(fj.a.mass(), fj.b.mass()), 1, 8);
  }
  addStage();
  start();
}
const _qa = new THREE.Quaternion(), _qr = new THREE.Quaternion();
// Each joint's stop and friction. Bent past its limit, a joint pushes back hard (scaled by
// the load it carries, so a whole hanging body can rest on one stop) and soaks up the
// speed it hit the stop with; inside the limit it only rubs, so it holds a pose you bend
// it into but still swings when you shake it.
const _ax = new THREE.Vector3(), _tq = new THREE.Vector3(), _rv = new THREE.Vector3();
function stepFlexJoints(dt) {
  if (window.__flexOff) return;
  const KLIM = 2600, CLIM = 70, FR = 9, FMAX = 30;
  for (const j of flexJoints) {
    _qa.copy(tq(j.a));
    _qr.copy(_qa).invert().multiply(tq(j.b)); // b relative to a, printed pose = identity
    if (_qr.w < 0) _qr.set(-_qr.x, -_qr.y, -_qr.z, -_qr.w);
    const angle = 2 * Math.acos(Math.min(_qr.w, 1));
    const s = Math.sqrt(Math.max(1 - _qr.w * _qr.w, 0));
    // the bend as a rotation vector in a's own frame (x: up/down, y: side to side)
    if (s > 1e-5) _rv.set(_qr.x / s, _qr.y / s, _qr.z / s).multiplyScalar(angle); else _rv.set(0, 0, 0);
    const pa = j.a.principalInertia(), pb = j.b.principalInertia();
    const I = Math.min((pa.x + pa.y + pa.z) / 3, (pb.x + pb.y + pb.z) / 3) * j.load;
    const wRel = tv(j.b.angvel()).sub(tv(j.a.angvel()));
    _tq.set(0, 0, 0);
    // past the elliptical stop: push back toward the nearest allowed bend
    const e = Math.hypot(_rv.x / j.lim, _rv.y / j.limY, _rv.z / j.lim);
    if (e > 1) {
      _ax.copy(_rv).multiplyScalar(1 - 1 / e).applyQuaternion(_qa); // the overshoot, in world
      const over = _ax.length(); _ax.divideScalar(over);
      const into = Math.max(0, wRel.dot(_ax));
      _tq.addScaledVector(_ax, -(KLIM * over + CLIM * into));
    }
    const ws = wRel.length();
    if (ws > 1e-6) _tq.addScaledVector(wRel, -Math.min(FR * ws, FMAX) / ws);
    _tq.multiplyScalar(I * dt);
    j.b.applyTorqueImpulse({ x: _tq.x, y: _tq.y, z: _tq.z }, true);
    j.a.applyTorqueImpulse({ x: -_tq.x, y: -_tq.y, z: -_tq.z }, true);
  }
}

// Spiral eggs: blades ordered by helical phase. Opening by e (0 shut, 1 open) raises blade i
// by e * i * step and turns it by twist * rise, i.e. slides it along its helical seam.
const spiral = { blades: [], e: 0, target: 0, twist: 0, step: 0, height: 0, lastTick: 0, anim: null };
function loadSpiral(gltf) {
  spiral.twist = gltf.scene.userData.twist || 0.4;
  const nodes = [...gltf.scene.children].sort((a, b) => a.userData.phase - b.userData.phase);
  const box = new THREE.Box3().setFromObject(gltf.scene);
  spiral.height = box.max.y - box.min.y;
  spiral.step = (spiral.height * (T.openRise || 1.5)) / Math.max(nodes.length - 1, 1);
  for (const o of nodes) {
    const pivot = new THREE.Group();
    o.userData.part = 'blade';
    o.traverse((c) => { if (c.isMesh) { c.material = c.material.clone(); bodyMats.push(c.material); } });
    styleMeshes(o);
    pivot.add(o);
    toyGroup.add(pivot);
    spiral.blades.push(pivot);
  }
  bodyMats.forEach((m) => m.color.copy(colorTarget));
  col(hullOf(nodes, new THREE.Vector3(), 6), toy);
  ballast(toy, null);
  ui.lid.hidden = false;
  updateTwistButton();
  start();
}
function updateTwistButton() { if (T.spiral) ui.lid.textContent = (spiral.anim ? spiral.anim.to : spiral.target) > 0.5 ? 'Twist shut' : 'Twist open'; }
function stepSpiral(dt) {
  if (!T.spiral) return;
  if (spiral.anim) {
    spiral.anim.t += dt;
    const k = Math.min(spiral.anim.t / 0.9, 1), ease = k < 0.5 ? 2 * k * k : 1 - Math.pow(-2 * k + 2, 2) / 2;
    spiral.target = spiral.anim.from + (spiral.anim.to - spiral.anim.from) * ease;
    if (k >= 1) spiral.anim = null;
  }
  spiral.e += (spiral.target - spiral.e) * (1 - Math.exp(-dt * 16));
  const n = spiral.blades.length;
  spiral.blades.forEach((b, i) => {
    const rise = spiral.e * i * spiral.step;
    b.position.y = rise;
    b.rotation.y = spiral.twist * rise;
  });
  // a soft plastic tick each time the blades slide past another notch
  const notch = Math.floor(spiral.e * (n - 1) * 2);
  if (notch !== spiral.lastTick) {
    if (S.time - (spiral.lastTickAt || 0) > 0.03) { sfx.slide(spiral.e); spiral.lastTickAt = S.time; }
    spiral.lastTick = notch;
  }
  // keep the tower in frame as it grows
  const rise = spiral.e * spiral.step * (n - 1);
  const want = 1.3 + rise * 0.5;
  view.target.y += (want - view.target.y) * (1 - Math.exp(-dt * 4));
  spiral.zoom = (spiral.zoom ?? 1) + (1 + rise / (spiral.height * 2.2) - (spiral.zoom ?? 1)) * (1 - Math.exp(-dt * 4));
  const d0 = view.dist; view.dist = d0 * spiral.zoom; placeCamera(); view.dist = d0;
}
function twistTo(to) { spiral.anim = { t: 0, from: spiral.target, to }; updateTwistButton(); }

function start() {
  spawn();
  document.getElementById('loading').classList.add('done');
  last = performance.now();
  requestAnimationFrame(loop);
}

// ───────────────────────── loop ─────────────────────────
let last = 0, acc = 0;
function loop(now) {
  const dt = Math.min((now - last) / 1000, 1 / 20); last = now;
  frame(dt);
  requestAnimationFrame(loop);
}
// Debug/test hook: advance n frames of 1/60 s without waiting for the browser.
window.__pg = { toy, camera, view, S, scene, world, toyGroup, RAPIER, units, buttons, flexJoints, flexRest, spiral, lid: () => lidBody, grab(i, p) { const b = units[i].rb; startGrab(b, tv(b.translation())); drag.target.set(p.x, p.y, p.z); }, release: () => endDrag(), aim(p) { drag.target.set(p.x, p.y, p.z); }, place: () => placeCamera(), flexSkip, fr, flexCore: () => flexCore, dragBody: () => drag.body, tick(n = 1) { for (let i = 0; i < n; i++) frame(1 / 60); } };

function frame(dt) {
  S.time += dt;
  acc += dt;
  let steps = 0;
  const step = world.timestep;
  // catch up on as much time as a frame can be long (loop caps dt at 1/20 s): the flexis' fine
  // 1/240 s steps need 12 of those, and stopping at 8 left them in slow motion whenever the
  // browser dropped under 30 fps
  const maxSteps = Math.ceil(1 / 20 / step - 1e-6);
  while (acc >= step && steps < maxSteps) {
    stepLidPull(step);
    stepGrab(step);
    if (!T.flexi) stepSelfRight(step);
    else flexRight(step);
    if (T.flexi && !fr.on) for (const u of units) {
      // speed limits: the end of a chain whips far faster than the hand moves it, and that
      // snap is what makes a flexi feel harsh
      const w = u.rb.angvel(), sp = Math.hypot(w.x, w.y, w.z);
      if (sp > 7) u.rb.setAngvel({ x: (w.x * 7) / sp, y: (w.y * 7) / sp, z: (w.z * 7) / sp }, true);
      const v = u.rb.linvel(), vs = Math.hypot(v.x, v.y, v.z);
      if (vs > 16) u.rb.setLinvel({ x: (v.x * 16) / vs, y: (v.y * 16) / vs, z: (v.z * 16) / vs }, true);
    }
    stepRolling(step);
    for (const u of units) leash(u.rb, step);
    if (lidBody) leash(lidBody, step);
    world.step(events, T.flexi ? flexHooks : undefined);
    drainContacts();
    stepPress(step);
    acc -= step; steps++;
  }
  if (steps === maxSteps) acc = 0;
  if (S.lidKick && S.time >= S.lidKick.at) {
    if (lidBody) {
      const v = lidBody.linvel();
      lidBody.setLinvel({ x: v.x + S.lidKick.v.x, y: v.y, z: v.z + S.lidKick.v.z }, true);
      lidBody.setAngvel({ x: S.lidKick.v.z * 0.6, y: 1, z: -S.lidKick.v.x * 0.6 }, true);
    }
    S.lidKick = null;
  }
  checkAutoSnap();
  checkNudge(dt);
  stepSnap(dt);
  stepSpiral(dt);

  for (const u of units) {
    // jiggle: the button lags behind its body's acceleration
    const vel = tv(u.rb.linvel());
    const aRaw = vel.clone().sub(u.prevVel).divideScalar(Math.max(dt, 1e-3));
    u.prevVel.copy(vel);
    if (aRaw.length() > 600) aRaw.setLength(600);
    u.accF.lerp(aRaw, 0.22);
    const a = u.accF.clone().applyQuaternion(tq(u.rb).invert());
    const K = 260, C = 12, s = reduceMotion ? 0 : 0.07 * (T.jiggle ?? 1);
    u.jigV.x += (-K * u.jig.x - C * u.jigV.x - a.x * s) * dt;
    u.jigV.z += (-K * u.jig.z - C * u.jigV.z - a.z * s) * dt;
    u.jigV.y += (-K * u.jig.y - C * u.jigV.y) * dt;
    u.jig.addScaledVector(u.jigV, dt);
    u.jig.clampScalar(-0.12, 0.12);
    u.group.position.copy(u.rb.translation());
    u.group.quaternion.copy(tq(u.rb));
  }
  for (const b of buttons) {
    const p = b.press, j = b.unit.jig, jw = T.jiggle ?? 1;
    b.pivot.position.set(b.seat.x + j.x * jw, b.seat.y - Math.max(p, -0.25) * PRESS_DEPTH, b.seat.z + j.z * jw);
    const sq = 1 - T.squash * Math.max(p, 0) + j.y * 0.5 * jw + (T.squash * 0.6) * Math.max(-p, 0);
    b.pivot.scale.set(1 / Math.sqrt(sq), sq, 1 / Math.sqrt(sq));
    b.pivot.rotation.set(j.z * 1.2 * jw, 0, -j.x * 1.2 * jw);
  }
  const p = S.press;
  if (squashers.length) {
    const k = 1 - T.squashAmount * Math.max(p, 0);
    for (const o of squashers) { o.scale.y = k; o.position.y = T.squashBase * (1 - k); }
  }
  if (T.lid) lidPivot.position.y = LID_SEAT + (S.lidOn ? S.lidLift : 0);
  if (drag.mode !== 'lid') S.lidLift *= 0.8;
  if (lidBody) { lidGroup.position.copy(lidBody.translation()); lidGroup.quaternion.copy(tq(lidBody)); }
  bodyMats.forEach((m) => m.color.lerp(colorTarget, 0.15));

  for (const u of units) u.group.updateMatrixWorld(true);
  stepHint();
  renderer.render(scene, camera);
}
